/**
 * end-game-overlay.ts — modal shown when `gameOutcome` flips to
 * `'victory'` or `'defeat'` (v3M12.2).
 *
 * Mounted by `session.ts` on outcome flip; sits at z-index 1200 so it
 * covers the in-game menu (1000) and any panel overlays. Style mirrors
 * `in-game-menu.ts` so the two read as the same UI system.
 *
 * Two button paths:
 *   - **Return to Main Menu** — primary; tears the session down. Same
 *     callback the in-game-menu uses, so the wiring is symmetric.
 *   - **Continue Playing** — secondary; just closes the overlay. The
 *     sim stays frozen via the `gameOutcome !== 'in-progress'` guard
 *     in `advanceTick`, but the canvas + camera stay responsive so
 *     the player can pan around and inspect the final frame.
 *
 * Stats card (per `docs/WIN_CONDITIONS.md` §5):
 *   - Game duration in ticks + wall-clock time (10 Hz sim → seconds = tick / 10)
 *   - Planets controlled at end (player count vs. total owned)
 *   - Tech researched count (player's `researchedTechs.size`)
 *   - Empires defeated (count of non-player empires with zero planets)
 *
 * Lifetime ship-build counter intentionally omitted in v3M12 — would
 * need a new `Empire.shipsBuiltLifetime` field. Logged for v3M11.
 */

import type { GameState } from '../state/types';
import { EMPIRE_NEUTRAL_ID } from '../state/types';

export type EndGameOutcome = 'victory' | 'defeat';

export interface EndGameOverlayCallbacks {
  onReturnToMainMenu: () => void;
  /** Closes the overlay; sim remains frozen, canvas stays interactive. */
  onContinue: () => void;
}

export interface EndGameOverlayHandle {
  destroy(): void;
}

const VICTORY_GREEN = '#63C77C'; // GRAPHICS §2.1 Success-green
const DEFEAT_RED = '#D24548'; // GRAPHICS §2.1 Hull-red

export function mountEndGameOverlay(
  parent: HTMLElement,
  state: GameState,
  outcome: EndGameOutcome,
  callbacks: EndGameOverlayCallbacks,
): EndGameOverlayHandle {
  const overlay = document.createElement('div');
  overlay.id = 'end-game-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(5, 6, 10, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1200',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
    // v4M1.11 — 300 ms opacity fade-in for the end-game overlay (slower
    // than the other overlays — the outcome is a heavy moment and a
    // gentler tail respects that). Destroy uses 150 ms to match the
    // other overlays so a quick dismiss feels snappy.
    opacity: '0',
    transition: 'opacity 300ms ease-out',
  } as Partial<CSSStyleDeclaration>);

  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    minWidth: '420px',
    padding: '32px 40px',
    background: '#0E131C',
    border: `2px solid ${outcome === 'victory' ? VICTORY_GREEN : DEFEAT_RED}`,
    borderRadius: '2px',
    alignItems: 'center',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(card);

  const banner = document.createElement('div');
  banner.textContent = outcome === 'victory' ? 'VICTORY' : 'DEFEAT';
  Object.assign(banner.style, {
    fontSize: '32px',
    fontWeight: '700',
    letterSpacing: '0.18em',
    color: outcome === 'victory' ? VICTORY_GREEN : DEFEAT_RED,
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(banner);

  const subtitle = document.createElement('div');
  subtitle.textContent =
    outcome === 'victory'
      ? 'You stand alone among the stars.'
      : 'Your last world has fallen.';
  Object.assign(subtitle.style, {
    fontSize: '13px',
    letterSpacing: '0.04em',
    color: '#7E8799',
    marginBottom: '12px',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(subtitle);

  // --- Stats card -----------------------------------------------------
  const stats = document.createElement('div');
  Object.assign(stats.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
    padding: '12px 16px',
    background: '#080C13',
    border: '1px solid #1F2735',
    borderRadius: '2px',
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(stats);

  for (const row of buildStatRows(state, outcome)) {
    stats.appendChild(makeStatRow(row.label, row.value));
  }

  // --- Buttons --------------------------------------------------------
  const buttonRow = document.createElement('div');
  Object.assign(buttonRow.style, {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
    width: '100%',
    justifyContent: 'flex-end',
  } as Partial<CSSStyleDeclaration>);
  card.appendChild(buttonRow);

  const continueBtn = makeButton('Continue Playing', false);
  continueBtn.title = 'Dismiss this dialog and keep playing the frozen final state.';
  continueBtn.addEventListener('click', callbacks.onContinue);
  buttonRow.appendChild(continueBtn);

  const returnBtn = makeButton('Return to Main Menu', true);
  returnBtn.title = 'Leave this game and return to the title screen.';
  returnBtn.addEventListener('click', callbacks.onReturnToMainMenu);
  buttonRow.appendChild(returnBtn);

  parent.appendChild(overlay);
  // v4M1.11 — opt the overlay into the fade by flipping opacity on the
  // next paint. The 300 ms ease-out lands the outcome banner gently.
  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });

  return {
    destroy(): void {
      // v4M1.11 — 150 ms fade-out (faster than the entry — a player
      // hitting Continue / Return wants the UI out of the way promptly).
      overlay.style.transition = 'opacity 150ms ease-out';
      overlay.style.opacity = '0';
      setTimeout(() => overlay.remove(), 150);
    },
  };
}

interface StatRow {
  label: string;
  value: string;
}

function buildStatRows(state: GameState, outcome: EndGameOutcome): StatRow[] {
  const ticks = state.tick;
  const totalSeconds = Math.floor(ticks / 10);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const durationStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  let playerPlanets = 0;
  let ownedPlanets = 0;
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) continue;
    ownedPlanets += 1;
    if (planet.ownerEmpireId === state.playerEmpireId) playerPlanets += 1;
  }

  const player = state.empires.get(state.playerEmpireId);
  const techCount = player?.researchedTechs.size ?? 0;

  // Empires defeated = non-player empires currently holding zero planets.
  // The eliminated rows still live in `state.empires` per
  // `advance-game-end.ts`; this just counts those whose planet tally is 0.
  const planetsByEmpire = new Map<number, number>();
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) continue;
    planetsByEmpire.set(
      planet.ownerEmpireId,
      (planetsByEmpire.get(planet.ownerEmpireId) ?? 0) + 1,
    );
  }
  let defeated = 0;
  for (const empire of state.empires.values()) {
    if (empire.id === state.playerEmpireId) continue;
    // v3M21.1 — synthetic neutral empire is not a competing empire on
    // the victory/defeat screen. Excluding it from the defeated tally
    // keeps the narrative focused on real AIs the player wiped out.
    if (empire.id === EMPIRE_NEUTRAL_ID) continue;
    if ((planetsByEmpire.get(empire.id) ?? 0) === 0) defeated += 1;
  }

  // On defeat the player's planet count is zero by definition; surfacing
  // it would just say "0 / N" with no reading value. Skip the row for
  // defeat outcomes and let the empire-defeated count carry the
  // narrative instead.
  const rows: StatRow[] = [
    { label: 'Game duration', value: `${ticks} ticks (${durationStr})` },
  ];
  if (outcome === 'victory') {
    rows.push({
      label: 'Planets controlled',
      value: `${playerPlanets} / ${ownedPlanets}`,
    });
  }
  rows.push({ label: 'Tech researched', value: String(techCount) });
  rows.push({ label: 'Empires defeated', value: String(defeated) });
  return rows;
}

function makeStatRow(label: string, value: string): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  } as Partial<CSSStyleDeclaration>);
  const lab = document.createElement('span');
  lab.textContent = label;
  Object.assign(lab.style, {
    color: '#7E8799',
    letterSpacing: '0.03em',
  } as Partial<CSSStyleDeclaration>);
  const val = document.createElement('span');
  val.textContent = value;
  Object.assign(val.style, {
    color: '#D7DEEA',
    fontWeight: '500',
  } as Partial<CSSStyleDeclaration>);
  row.appendChild(lab);
  row.appendChild(val);
  return row;
}

function makeButton(label: string, primary: boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  Object.assign(btn.style, {
    padding: '10px 22px',
    background: primary ? '#2B4A7A' : '#0E131C',
    color: primary ? '#FFFFFF' : '#D7DEEA',
    border: `1px solid ${primary ? '#3F6FA8' : '#2A3344'}`,
    borderRadius: '2px',
    fontFamily: 'inherit',
    fontSize: '13px',
    letterSpacing: '0.06em',
    cursor: 'pointer',
  } as Partial<CSSStyleDeclaration>);
  return btn;
}
